import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import useAsyncData from '../hooks/useAsyncData';
import useStaleWarning from '../hooks/useStaleWarning';
import useAutoRefresh from '../hooks/useAutoRefresh';

const SystemHealth = () => {
  const { data: health, loading, error, retry } = useAsyncData('/api/health');
  const { isStale, lastUpdated } = useStaleWarning(health);
  useAutoRefresh(retry);

  const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '–';

  if (loading) {
    return (
      <ErrorBoundary>
        <article className="panel health-panel" aria-busy="true">
          <h2>System Health</h2>
          <div className="skeleton">
            <div className="skeleton-line" />
            <div className="skeleton-line" />
            <div className="skeleton-line" />
            <div className="skeleton-line" />
            <div className="skeleton-line" />
          </div>
        </article>
      </ErrorBoundary>
    );
  }

  if (error) {
    return (
      <ErrorBoundary>
        <article className="panel health-panel" aria-live="assertive">
          <h2>System Health</h2>
          <p className="error-msg">Failed to load health metrics.</p>
          <button type="button" className="btn-retry" onClick={retry}>Retry</button>
        </article>
      </ErrorBoundary>
    );
  }

  if (!health) {
    return (
      <ErrorBoundary>
        <article className="panel health-panel">
          <h2>System Health</h2>
          <p>No health data available.</p>
        </article>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <article className="panel health-panel" aria-label="System health" aria-busy="false">
        <div className="panel-header">
          <h2>{health.hostname || 'Unknown Host'}</h2>
          <div className="panel-meta">
            <span className="last-updated" aria-label="Last updated">
              Last updated: {fmtTime(lastUpdated)}
            </span>
            {isStale && (
              <span className="badge badge-stale" role="status">Stale</span>
            )}
            <button type="button" className="btn-icon btn-refresh" onClick={retry} aria-label="Refresh health">
              ↻
            </button>
          </div>
        </div>
        <dl className="health-metrics">
          <div><dt>Gateway</dt><dd className={`state-${health.gateway}`}>{health.gateway}</dd></div>
          <div><dt>CPU</dt><dd>{health.cpu}%</dd></div>
          <div><dt>Memory</dt><dd>{health.memory?.percent || 0}%</dd></div>
          <div><dt>Disk</dt><dd>{health.disk?.percent || 0}%</dd></div>
          <div><dt>Uptime</dt><dd>{health.uptime}h</dd></div>
        </dl>
      </article>
    </ErrorBoundary>
  );
};

export default SystemHealth;
